import type { ChatConversationSummary } from '@mogzo/shared';
import { MoreHorizontal } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Avatar } from '../community/Avatar';

/** A person's initials, or two of a group's people overlapping. */
export function ChatAvatar({
  chat,
  size = 'lg',
}: {
  chat: Pick<ChatConversationSummary, 'kind' | 'name' | 'people'>;
  size?: 'md' | 'lg';
}) {
  if (chat.kind === 'direct') return <Avatar name={chat.name} size={size} />;
  const [a, b] = chat.people;
  return (
    <span
      aria-hidden="true"
      className={`relative shrink-0 ${size === 'lg' ? 'size-11' : 'size-10'}`}
    >
      <span className="absolute top-0 left-0">
        <Avatar name={a?.username ?? chat.name} size="sm" ring />
      </span>
      <span className="absolute right-0 bottom-0">
        <Avatar name={b?.username ?? chat.name} size="sm" ring />
      </span>
    </span>
  );
}

export interface MenuItem {
  label: string;
  icon: typeof MoreHorizontal;
  onSelect: () => void;
  danger?: boolean;
}

/** A "More" button with a small menu under it; closes on a click outside or Escape. */
export function PopMenu({
  label,
  items,
  align = 'right',
  small = false,
}: {
  label: string;
  items: (MenuItem | 'separator')[];
  align?: 'left' | 'right';
  small?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((was) => !was)}
        className={`flex items-center justify-center rounded-full text-ink hover:bg-field ${small ? 'size-8 text-muted' : 'size-10'} ${open ? 'bg-field' : ''}`}
      >
        <MoreHorizontal size={small ? 18 : 20} strokeWidth={1.9} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={id}
          role="menu"
          aria-label={label}
          className={`absolute top-full z-30 mt-1 flex w-60 flex-col rounded-[14px] border border-line bg-card p-1.5 shadow-[0_8px_24px_rgba(27,21,47,0.14)] ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {items.map((item, i) =>
            item === 'separator' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: separators have nothing else to key on
              <hr key={`s${i}`} className="mx-1 my-1.5 border-divider" />
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={`flex h-11 items-center gap-3 rounded-[10px] px-3 text-left text-[15px] font-medium hover:bg-field ${item.danger ? 'text-[#c23a32]' : ''}`}
              >
                <item.icon size={18} strokeWidth={1.9} aria-hidden="true" />
                {item.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

const TONES = {
  danger: 'bg-[#fbeceb] [&>svg]:text-[#c23a32]',
  info: 'bg-[#ebf0fc] [&>svg]:text-[#2e5bd0]',
  warning: 'bg-[#fbf1de] [&>svg]:text-[#9a5b00]',
} as const;

/** A short message in a tinted box, with its icon: a refused message, links, chat turned off. */
export function Say({
  tone,
  icon: Icon,
  children,
}: {
  tone: keyof typeof TONES;
  icon: typeof MoreHorizontal;
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-[14px] px-3.5 py-2.5 text-sm leading-5 ${TONES[tone]}`}
    >
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" className="mt-px shrink-0" />
      <span>{children}</span>
    </div>
  );
}
